import { Injectable } from '@angular/core';

import * as moment from 'moment';

import { EStorageProperties } from '../consts/enums';
import { DateTimeService } from './date-time.service';

interface IDataRecord<T> {
  data: T;
  createdAt: string;
  onlyForCurrentSession: boolean;
  onlyForCurrentDay?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor(
    private dateTime: DateTimeService,
  ) {}

  public write = <T>(
    property: EStorageProperties,
    value: T,
    onlyForCurrentSession = false,
    onlyForCurrentDay = false,
  ): void => {
    const record: IDataRecord<T> = {
      data: value,
      createdAt: moment().toISOString(),
      onlyForCurrentSession,
      onlyForCurrentDay
    };
    localStorage.setItem(property, JSON.stringify(record));
  }

  public read = <T>(property: EStorageProperties): T | null => {
    let storedValue: unknown;
    try {
      storedValue = JSON.parse(localStorage.getItem(property));
    } catch {
      this.clean(property);
      return null;
    }
    if (storedValue === null) {
      return null;
    }
    if (!this.isDataRecord<T>(storedValue)) {
      this.clean(property);
      return null;
    }
    const dataRecord = storedValue;
    if (dataRecord.onlyForCurrentSession) {
      const currentSessionDate = this.read<string>(EStorageProperties.CurrentSessionDate);
      if (Boolean(currentSessionDate) && dataRecord.createdAt < currentSessionDate) {
        this.clean(property);
        return null;
      }
    }
    if (
      Object.prototype.hasOwnProperty.call(dataRecord, 'onlyForCurrentDay')
      && dataRecord.onlyForCurrentDay
      && !this.dateTime.isToday(dataRecord.createdAt)
    ) {
      this.clean(property);
      return null;
    }
    return dataRecord.data;
  }

  public clean = (property: EStorageProperties): void => {
    localStorage.setItem(property, null);
  }

  private isDataRecord = <T>(value: unknown): value is IDataRecord<T> => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }
    const record = value as Partial<IDataRecord<T>>;
    const hasOwnProperty = (property: keyof IDataRecord<T>): boolean =>
      Object.prototype.hasOwnProperty.call(record, property);
    return hasOwnProperty('data')
      && hasOwnProperty('createdAt')
      && typeof record.createdAt === 'string'
      && hasOwnProperty('onlyForCurrentSession')
      && typeof record.onlyForCurrentSession === 'boolean'
      && (
        !hasOwnProperty('onlyForCurrentDay')
        || typeof record.onlyForCurrentDay === 'boolean'
      );
  }
}
